import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import { handleChunkLoadError } from '@/utils/errors.ts';
import { sendMetricIncrement } from '@/app/components/hotel-detail/metrics.ts';
import { API_CALL_PREFIX } from '@/app/components/search/utils/api/apiUtils.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import { getIsApp } from '@/app/components/search/Utilities.ts';
import type {
  UncaughtErrorCount,
  UncaughtKind,
  UncaughtOrigin,
} from '@/app/common/components/ErrorBoundary/uncaughtErrorTypes.ts';

const oneLine = (value?: string | null) => value?.replace(/\n/g, ' || ') || undefined;

type LogClientErrorProps = {
  codeSourceName: string;
  error: Error;
  details?: (string | undefined)[];
};

const logClientError = ({ codeSourceName, error, details = [] }: LogClientErrorProps) => {
  // A chunk error the guard refused to reload for means the last reload did not help, so it stays an ERROR
  const isReloading = handleChunkLoadError(error);

  clientAppLog({
    level: isReloading ? 'WARN' : 'ERROR',
    codeSourceName,
    logMessage: [codeSourceName, `error:${error?.name}`, `message:${error?.message}`, ...details]
      .filter(Boolean)
      .join(' | '),
    url: window?.location?.href,
  });
};

export const logFromErrorBoundary = (
  error: Error,
  id: string,
  stackTraceEnabled: boolean,
  componentStack?: string | null
) => {
  logClientError({
    codeSourceName: `ErrorBoundary:${id}`,
    error,
    details: stackTraceEnabled
      ? [
          `stackTrace:${oneLine(error?.stack) || 'No stack trace'}`,
          `componentStack:${oneLine(componentStack) || 'No component stack'}`,
        ]
      : [],
  });

  sendMetricIncrement(`app.js-error.error-boundary.${id}.triggered`);
};

const isInApp = () => getIsApp(Object.fromEntries(new URLSearchParams(window.location.search)));

export const countUncaughtError = (kind: UncaughtKind, origin: UncaughtOrigin) => {
  const count: UncaughtErrorCount = { kind, origin, inApp: isInApp() };
  navigator.sendBeacon?.(`${API_CALL_PREFIX}${API_NAME.UNCAUGHT_ERROR_COUNT}`, JSON.stringify(count));
};

// No id and no component stack to localise these, so the stack is the only locator: always send it.
export const logUncaughtError = (kind: UncaughtKind, origin: UncaughtOrigin, error: Error, source?: string) => {
  logClientError({
    codeSourceName: `Uncaught:${origin === 'tsx' ? kind : `${kind}-${origin}`}`,
    error,
    details: [source, `stackTrace:${oneLine(error?.stack) || 'No stack trace'}`],
  });

  countUncaughtError(kind, origin);
};
